import { Capacitor } from "@capacitor/core";

/**
 * Self-healing recovery from stale-deploy chunk errors (issue #180).
 *
 * A new release replaces the asset manifest on Cloudflare and purges the
 * previous build's hashed chunks. A tab still running the old bundle — Safari's
 * aggressive bfcache is the usual culprit — that then lazily imports a
 * now-missing route chunk gets the SPA fallback `index.html` served as
 * `text/html`, and the dynamic `import()` fails ("...is not a valid JavaScript
 * MIME type for module script"). TanStack Router catches that and shows its
 * error screen, when a plain reload (which re-fetches the current `index.html`
 * and its up-to-date chunk hashes) would fix it.
 *
 * Vite dispatches a cancelable `vite:preloadError` event on `window` when a
 * dynamic import fails, and only rethrows the error if the event is *not*
 * `preventDefault()`-ed. So on the first such error we `preventDefault()` (which
 * suppresses the router error component and the Sentry noise that would follow)
 * and reload once. A `sessionStorage` timestamp guards against a reload loop if
 * the fresh build is itself broken: within the guard window we do nothing and
 * let the error propagate to the router's `defaultErrorComponent` (and Sentry),
 * so a genuinely broken deploy surfaces instead of reloading forever.
 */

const RELOAD_GUARD_KEY = "provender:stale-deploy-reloaded-at";
const RELOAD_GUARD_WINDOW_MS = 10_000;

function recentlyReloaded(now: number): boolean {
  try {
    const raw = sessionStorage.getItem(RELOAD_GUARD_KEY);
    if (raw === null) return false; // never reloaded this session
    const last = Number(raw);
    return Number.isFinite(last) && now - last < RELOAD_GUARD_WINDOW_MS;
  } catch {
    // sessionStorage can throw (private mode, disabled). Treat as "not recently
    // reloaded" so a genuine stale deploy still self-heals at least once.
    return false;
  }
}

function markReloaded(now: number): void {
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(now));
  } catch {
    // Ignore — worst case the reload isn't recorded and we might reload twice,
    // which is still bounded by the guard window.
  }
}

export interface StaleDeployReloadDeps {
  target?: Pick<EventTarget, "addEventListener">;
  reload?: () => void;
  now?: () => number;
  isNative?: () => boolean;
}

/**
 * Installs the `vite:preloadError` handler. Dependencies are injectable for
 * testing; production wiring uses the real `window`, `location.reload`, clock,
 * and platform check.
 */
export function installStaleDeployReload(
  deps: StaleDeployReloadDeps = {},
): void {
  const target = deps.target ?? window;
  const reload = deps.reload ?? (() => window.location.reload());
  const now = deps.now ?? (() => Date.now());
  const isNative = deps.isNative ?? (() => Capacitor.isNativePlatform());

  target.addEventListener("vite:preloadError", (event) => {
    // Native (Capacitor) assets are baked into the App Store binary, so a failed
    // import is a real missing-chunk bug and reloading the WebView can't fetch
    // new code. Let it fall through to the router error component (which reports
    // to Sentry). Mirrors the web-only gating of the update prompt (#177).
    if (isNative()) return;

    const at = now();
    if (recentlyReloaded(at)) return; // reload already tried; let it propagate

    event.preventDefault(); // handled: suppress the router error + Sentry noise
    markReloaded(at);
    reload();
  });
}
